import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { GenericExternalLink } from "@components/generic-external-link";
import { GenericTooltip } from "@components/tooltip-container";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type DomainCollectionQuery } from "@north-beam/nb-client-side";

import { SideNavContext, type SubNavItem } from "..";
import { Error } from "../error";
import {
  isVisible,
  scrollToSubStepElement,
} from "../onboarding-nav/scroll-utils";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { InfoPanel } from "./components/info-panel";
import DnsEntry from "./dns-entry";
import {
  VerifySetup,
  useGetVerifySetupDnsTitle,
} from "./dns-entry/verify-setup";
import { DOMAIN_COLLECTION_QUERY } from "./domain-collection";
import { Step, type StepChildProps } from "./step";
import { StepCompletionConfirmation } from "./step-completion-confirmation";
import { StepContainer } from "./step-container";

export const DnsEntryStep: FC<StepChildProps> = ({
  nextStep,
  previousStep,
  goToNamedStep,
  isMarkedComplete,
  toggleStepComplete,
  ...props
}) => {
  const verifySteptitle = useGetVerifySetupDnsTitle();
  const { stepDictionary } = useOnboardingStep();

  const stepIndex = stepDictionary["dns-setup"].index;
  const SUBNAV_ITEMS = useMemo(() => {
    return {
      google: [
        {
          label: `${stepIndex}a.  Please select the DNS provider that you use`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please configure your Google DNS solution`,
          subStep: "b",
        },
      ],
      other: [
        {
          label: `${stepIndex}a.  Please select the DNS provider that you use`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please configure your Generic DNS solution`,
          subStep: "b",
        },
      ],
      go_daddy: [
        {
          label: `${stepIndex}a.  Please select the DNS provider that you use`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please configure your Go Daddy DNS solution`,
          subStep: "b",
        },
      ],
      cloudflare: [
        {
          label: `${stepIndex}a.  Please select the DNS provider that you use`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please configure your Cloudflare DNS solution`,
          subStep: "b",
        },
      ],
    };
  }, []);

  const { hasFeatureFlag } = useFeatureFlags();
  const { setSubNavItems } = useContext(SideNavContext);

  const [stepMetadata, setStepMetadata] = useState(props.stepMetadata || {});
  const updateStepMetadata = (stepMetadata: any) => {
    setStepMetadata(stepMetadata);
    props.updateStepMetadata(stepMetadata);
  };

  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });

  useEffect(() => {
    if (searchParams.noScroll) {
      setSearchParams({ noScroll: false });
    }
  }, []);

  const {
    data,
    loading,
    error: domainCollectionError,
  } = useNorthbeamQuery<DomainCollectionQuery>(DOMAIN_COLLECTION_QUERY);
  const domainName = data?.me.domainName;

  useEffect(() => {
    if (loading || !domainName || !!domainCollectionError) {
      return;
    }

    const subNavItems = [
      ...((SUBNAV_ITEMS[
        stepMetadata.provider as keyof typeof SUBNAV_ITEMS
      ] as SubNavItem[]) || []),
      {
        subStep: "c",
        label: verifySteptitle,
      },
    ];

    setSubNavItems(subNavItems);
  }, [
    setSubNavItems,
    stepMetadata.provider,
    domainName,
    loading,
    domainCollectionError,
    SUBNAV_ITEMS,
    hasFeatureFlag,
    verifySteptitle,
  ]);

  const aScrollRef = useRef<(() => void) | null>(null);
  const bScrollRef = useRef<(() => void) | null>(null);
  const cScrollRef = useRef<(() => void) | null>(null);

  const aTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        aScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "a");
      }
    },
    [searchParams],
  );

  const bTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        bScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "b");
      }
    },
    [searchParams],
  );

  const cTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        cScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "c");
      }
    },
    [searchParams],
  );

  const { designElementManager } = useDesignElementManager();

  return (
    <StepContainer
      onScrollStopped={() => {
        if (isVisible(aScrollRef, 20)) {
          setSearchParams({
            subStep: "a",
            noScroll: true,
          });
        } else if (isVisible(cScrollRef, 20)) {
          setSearchParams({
            subStep: "c",
            noScroll: true,
          });
        } else if (isVisible(bScrollRef, 20)) {
          setSearchParams({
            subStep: "b",
            noScroll: true,
          });
        }
      }}
      hideFooter={loading || !!domainCollectionError}
      knowledgeBaseLink="https://docs.northbeam.io/docs/link-dns"
      knowledgeBaseText="Link DNS"
      title={`${stepIndex}. Configure DNS`}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.DOMAIN,
        });
        previousStep && previousStep();
      }}
      onMoveForwardButtonClicked={() => {
        setSearchParams({
          step: Steps.PIXEL_PLACEMENT,
        });
        nextStep && nextStep();
      }}
      stepCompletionConfirmation={
        stepMetadata.provider &&
        !domainCollectionError && (
          <div className="w-full">
            <GenericTooltip
              showTooltip={!stepMetadata?.isDNSConnected}
              content={`${
                domainName
                  ? "DNS connection must be verified"
                  : "Step Add Domain must be complete"
              } before the step can be marked as ‘Complete’.`}
              placement="top-start"
              noInfoCircle
            >
              <StepCompletionConfirmation
                labelSuffix="configuring your DNS."
                nextStep={() => {
                  setSearchParams({
                    step: Steps.PIXEL_PLACEMENT,
                  });
                  nextStep && nextStep();
                }}
                step={Steps.DNS_SETUP}
                toggleStepComplete={toggleStepComplete}
                disabled={!stepMetadata?.isDNSConnected}
              />
            </GenericTooltip>
          </div>
        )
      }
    >
      {domainCollectionError ? (
        <Error
          className="border bg-white"
          content={
            <div className="text-2xl font-semibold">
              <p>There was an error loading your settings.</p>
              <p>Please refresh the page to try again.</p>
              <InfoPanel label="Need help?" className="mt-2 py-4 px-6">
                <GenericExternalLink
                  href="https://www.northbeam.io/submit-a-support-ticket"
                  className="border border-gray-0 bg-white px-4 py-[6px] inline-flex items-center gap-2 rounded-2xl no-underline
                  hover:bg-cosmic-3 hover:border-cosmic+1"
                >
                  <i
                    className="fa-envelope fa-regular"
                    style={{ color: "#7081FF" }}
                  ></i>
                  <div className="text-black uppercase font-semibold text-xs">
                    Contact us
                  </div>
                </GenericExternalLink>
              </InfoPanel>
            </div>
          }
        />
      ) : (
        <Step
          nextStepButtonText={
            stepMetadata.provider !== ""
              ? `${stepDictionary["pixel-placement"].index}. Complete Pixel Placement`
              : undefined
          }
          nextStep={
            stepMetadata.provider !== ""
              ? () => {
                  setSearchParams({
                    step: Steps.PIXEL_PLACEMENT,
                  });
                  nextStep && nextStep();
                }
              : undefined
          }
          loading={loading}
        >
          {!domainName ? (
            <div className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10">
              <div className="text-2xl font-semibold leading-loose pb-6">
                Please complete Add Domain Step before configuring DNS
              </div>
              You must complete{" "}
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  setSearchParams({ step: Steps.DOMAIN });
                  goToNamedStep?.(Steps.DOMAIN);
                }}
              >
                Step Add Domain
              </a>{" "}
              before this step can be completed.
            </div>
          ) : (
            <>
              <div
                ref={aTitleRef}
                className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex scroll-m-10"
              >
                <div className="flex-col justify-start items-start gap-2 flex">
                  <div className="text-slate-900 text-2xl font-semibold leading-loose">
                    {stepIndex}a. Please select the DNS provider that you use
                  </div>
                </div>
                <div className="self-stretch justify-start items-start gap-[27px] inline-flex">
                  {[
                    {
                      name: "google",
                      label: (
                        <>
                          Google
                          <br />
                          Domains
                        </>
                      ),
                      designElementName: "google-domains",
                    },
                    {
                      name: "cloudflare",
                      label: "Cloudflare",
                      designElementName: "cloudflare",
                    },
                    {
                      name: "go_daddy",
                      label: "GoDaddy",
                      designElementName: "go-daddy",
                    },
                    {
                      name: "other",
                      label: (
                        <>
                          Other
                          <br />
                          (Generic DNS)
                        </>
                      ),
                      designIcon: (
                        <div className=" h-8 w-8 rounded-full bg-cosmic-4 border flex justify-center items-center border-gray-1">
                          <i className="fa-regular fa-server text-cosmic" />
                        </div>
                      ),
                    },
                  ].map((provider) => (
                    <GenericTooltip
                      content="Uncheck the 'complete' checkbox in order to change your choice."
                      noInfoCircle
                      showTooltip={isMarkedComplete}
                      placement="top"
                      key={provider.name}
                    >
                      <button
                        key={provider.name}
                        disabled={isMarkedComplete}
                        onClick={() => {
                          updateStepMetadata({
                            provider: provider.name,
                          });
                          setSearchParams({
                            subStep: "b",
                            noScroll: false,
                          });
                        }}
                        className={clsx(
                          "w-40 h-[90px] flex-col justify-center items-center gap-2 inline-flex rounded-lg",
                          {
                            " bg-gray-3": isMarkedComplete,
                            "hover:border-cosmic+0": !isMarkedComplete,
                            "border-cosmic+0 border-2":
                              stepMetadata.provider === provider.name,
                            "border-gray-0 border":
                              stepMetadata.provider !== provider.name,
                          },
                        )}
                      >
                        <div className="w-8 h-8 flex justify-center">
                          {provider.designElementName
                            ? designElementManager
                                .getDesignElement(provider.designElementName)
                                .getComponent({ size: "32" })
                            : provider.designIcon}
                        </div>
                        <div className="self-stretch h-7 text-center text-sm font-normal leading-[14px] flex justify-center items-center">
                          {provider.label}
                        </div>
                      </button>
                    </GenericTooltip>
                  ))}
                </div>
                <div className="self-stretch h-28 p-6 bg-slate-50 rounded-lg border border-neutral-300 flex-col justify-end items-start gap-4 flex">
                  <div className="justify-center items-center gap-2 flex">
                    <div className="w-[20px] h-[20px] bg-cosmic border border-black rounded-full flex justify-center items-center">
                      <i
                        className="fa-solid fa-user-plus fa-2xs"
                        style={{ color: "#ffffff" }}
                      ></i>
                    </div>
                    <div className="grow shrink basis-0 text-slate-900 text-lg font-semibold leading-normal">
                      Add an account manager to assist with this step
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      goToNamedStep && goToNamedStep("account-managers");
                      setSearchParams({
                        step: Steps.ACCOUNT_MANAGERS,
                        subStep: "",
                      });
                    }}
                    className="justify-start items-end gap-4 inline-flex"
                  >
                    <div
                      className="px-4 py-1.5 bg-white rounded-2xl border border-neutral-300
              justify-center items-center gap-2.5 flex hover:border-cosmic+0"
                    >
                      <div className="text-slate-950 text-xs font-semibold uppercase leading-tight tracking-tight">
                        Invite an account manager
                      </div>
                    </div>
                  </button>
                </div>
              </div>
              <div ref={bTitleRef} className="scroll-m-10">
                <DnsEntry
                  goToNamedStep={goToNamedStep}
                  selectedProvider={stepMetadata.provider}
                />
              </div>
              {stepMetadata.provider && (
                <VerifySetup
                  subStepRef={cTitleRef}
                  isSetupConfirmed={stepMetadata?.isSetupConfirmed}
                  setSetupConfirmed={(isSetupConfirmed: boolean) => {
                    updateStepMetadata({
                      ...stepMetadata,
                      isSetupConfirmed,
                    });
                  }}
                  isDNSConnected={stepMetadata?.isDNSConnected}
                  setDNSConnected={(isDNSConnected: boolean) => {
                    updateStepMetadata({
                      ...stepMetadata,
                      isDNSConnected,
                    });
                  }}
                  disabled={isMarkedComplete}
                />
              )}
            </>
          )}
        </Step>
      )}
    </StepContainer>
  );
};
